import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PipelineProjectService, {
  type InventoryItem,
  type PhaseCrewMember,
  type PhaseCrewSlot,
  type PhaseEquipmentSlot,
  type PhaseEquipmentTypeSlot,
  type PhaseEquipmentUnit,
} from '@frontend/AppSettings/services/PipelineProjectService';
import PhaseRail from '@frontend/Components/PhaseRail/PhaseRail';
import '@frontend/Components/BidTable/BidTable.css';

/**
 * Phase planning — ONE section for the optional layer above the matter roster (Build R,
 * plan team-bid-parity-and-fleet-registry): the bid's rail idiom on the left (phases, with
 * a money-free coverage figure), the SELECTED phase's crew AND equipment detail on the
 * right. This absorbs PhaseCrewPanel and PhaseEquipmentPanel whole: their doctrines, their
 * disclosure strings, their mutations and their walls are transplanted, not rethought.
 *
 * RECORD-ONLY, both axes: it drives no planned figure, no bid-vs-actual, no ticket. It
 * never nags — "0 of 3 named" is PLAIN TEXT with no badge, no colour, no warning; partial
 * is the resting state; over-assignment is DISCLOSED ("4 of 3 · over plan"), never refused.
 *
 * Optionality is a NETWORK promise, now with ONE gate where there were two: collapsed by
 * default, and all four reads (phases, crew, equipment, fleet) fire only on expand. The
 * query keys are BYTE-IDENTICAL to the old panels' — PhaseReadinessStrip shares
 * ['ProjectPhaseCrew'|'ProjectPhaseEquipment', matterid] verbatim (a near-miss key doubles
 * the fetch), and the roster's removal flow invalidates ['ProjectPhaseCrew', matterid].
 *
 * THE RAIL FIGURE (decision 3): "5/9 · 2/4" — hands then iron, named over planned, muted
 * plain text. The counting rule is the PANELS' OWN detail summed: raw assignment rows
 * grouped under each PLANNED catalog- or label-keyed slot (stranded people and retired
 * iron included — the rail must agree with the "4 of 3 · over plan" beside it), over the
 * sum of those slots' planned. Type slots are target-less by doctrine and enter NEITHER
 * side — an iron figure that counted them would mint exactly the forbidden quota. No-slot
 * and not-in-plan rows carry no denominator in the detail, so they carry none here.
 *
 * The crew pool is the MATTER TEAM (the roster passed down); the equipment pool is the
 * FLEET restricted to ASSET-TAGGED rows (0020's qty = 1 doctrine). All the old panels'
 * disclosures survive verbatim: "no longer on the team", retired, "different catalog
 * type", "· over plan", "· not in plan", "seen on N day sheets" (zero renders nothing),
 * frozen loads on type slots.
 */
export interface PhaseCrewTeamMember {
  employeeId: string;
  name: string;
}

interface PhasePlanningSectionProps {
  matterid: string;
  /** The matter roster (lead + associates) — the only people who may be assigned. */
  teamMembers: PhaseCrewTeamMember[];
  mayManage: boolean;
}

/** Plain text, both states — the whole no-nag doctrine in one line. */
const countText = (named: number, planned: number): string =>
  (named > planned ? `${named} of ${planned} · over plan` : `${named} of ${planned} named`);

const NO_ROLE = '';
const NO_SLOT = '';
const SUMMARY = 'summary';

const unitLabel = (name: string | null, assetTag: string | null): string => {
  const tag = (assetTag ?? '').trim();
  return tag ? `${name ?? tag} (${tag})` : (name ?? '');
};

/** A catalog-keyed slot is an equipment_catalog id; label- and type-keyed slots have no
 * catalog type to match — treating a `type:` key as a uuid would wear every type-slot member
 * with a false "different catalog type" disclosure (Build T). */
const isCatalogKey = (key: string): boolean =>
  key !== NO_SLOT && !key.startsWith('label:') && !key.startsWith('type:');

/** The frozen loads as muted disclosure — zero renders nothing (the sheet-count idiom). */
const loadsText = (loads: PhaseEquipmentTypeSlot['loads']): string => {
  const parts = [
    loads.heavy > 0 ? `${loads.heavy} heavy` : '',
    loads.legal > 0 ? `${loads.legal} legal` : '',
    loads.pickup > 0 ? `${loads.pickup} pickup` : '',
  ].filter(Boolean);
  return parts.length ? `${parts.join(', ')} loads` : '';
};

interface Coverage {
  crewNamed: number;
  crewPlanned: number;
  ironNamed: number;
  ironPlanned: number;
}

/** Fractional planned sums (F8) render as-is — JS prints 2.5 as 2.5. */
const coverageText = (c: Coverage): string =>
  `${c.crewNamed}/${c.crewPlanned} · ${c.ironNamed}/${c.ironPlanned}`;

const PhasePlanningSection = (
  { matterid, teamMembers, mayManage }: PhasePlanningSectionProps,
): JSX.Element => {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<string>(SUMMARY);

  // Crew action state — kept ABOVE the render gate so collapsing HIDES rather than clears
  // (the old panels' review-R6 rule: re-expanding restores the last action's result).
  const [crewPicks, setCrewPicks] = useState<Record<string, string>>({});
  const [rolePicks, setRolePicks] = useState<Record<string, string>>({});
  const [crewError, setCrewError] = useState<string | null>(null);

  // Equipment action state — same rule.
  const [unitPicks, setUnitPicks] = useState<Record<string, string>>({});
  const [slotPicks, setSlotPicks] = useState<Record<string, string>>({});
  const [equipError, setEquipError] = useState<string | null>(null);

  // Build L: "+ Assign person"/"+ Assign unit" open the existing picker form (the bid's
  // add-affordance idiom, closed by default). Above the gate, like every action state.
  const [crewFormOpen, setCrewFormOpen] = useState<Record<string, boolean>>({});
  const [equipFormOpen, setEquipFormOpen] = useState<Record<string, boolean>>({});

  // Fetched only on expand — the optionality promise made at the network layer, now ONE
  // gate for all four reads. Keys byte-identical to the old panels' (the strip shares them).
  const phases = useQuery({
    queryKey: ['ProjectPhases', matterid],
    queryFn: () => PipelineProjectService.getPhases(matterid),
    enabled: expanded,
  });
  const crew = useQuery({
    queryKey: ['ProjectPhaseCrew', matterid],
    queryFn: () => PipelineProjectService.getPhaseCrew(matterid),
    enabled: expanded,
  });
  const equipment = useQuery({
    queryKey: ['ProjectPhaseEquipment', matterid],
    queryFn: () => PipelineProjectService.getPhaseEquipment(matterid),
    enabled: expanded,
  });
  const fleet = useQuery({
    queryKey: ['ProjectInventory', matterid],
    queryFn: () => PipelineProjectService.getInventory(matterid),
    enabled: expanded,
  });

  const crewByPhase = useMemo(() => {
    const map = new Map<string, PhaseCrewMember[]>();
    (crew.data?.assignments ?? []).forEach((m) => {
      const list = map.get(m.project_phase_id) ?? [];
      list.push(m);
      map.set(m.project_phase_id, list);
    });
    return map;
  }, [crew.data]);

  const crewSlotsByPhase = useMemo(() => {
    const map = new Map<string, PhaseCrewSlot[]>();
    (crew.data?.slots ?? []).forEach((s) => {
      const list = map.get(s.project_phase_id) ?? [];
      list.push(s);
      map.set(s.project_phase_id, list);
    });
    return map;
  }, [crew.data]);

  const unitsByPhase = useMemo(() => {
    const map = new Map<string, PhaseEquipmentUnit[]>();
    (equipment.data?.assignments ?? []).forEach((u) => {
      const list = map.get(u.project_phase_id) ?? [];
      list.push(u);
      map.set(u.project_phase_id, list);
    });
    return map;
  }, [equipment.data]);

  const equipSlotsByPhase = useMemo(() => {
    const map = new Map<string, PhaseEquipmentSlot[]>();
    (equipment.data?.slots ?? []).forEach((s) => {
      const list = map.get(s.project_phase_id) ?? [];
      list.push(s);
      map.set(s.project_phase_id, list);
    });
    return map;
  }, [equipment.data]);

  const typeSlotsByPhase = useMemo(() => {
    const map = new Map<string, PhaseEquipmentTypeSlot[]>();
    (equipment.data?.type_slots ?? []).forEach((s) => {
      const list = map.get(s.project_phase_id) ?? [];
      list.push(s);
      map.set(s.project_phase_id, list);
    });
    return map;
  }, [equipment.data]);

  // The pool: asset-tagged rows only — the tagged row IS the unit (0020).
  const units = useMemo(
    () => (fleet.data ?? []).filter((i: InventoryItem) => (i.asset_tag ?? '').trim() !== ''),
    [fleet.data],
  );

  // The rail's money-free coverage (decision 3) — see the docblock for the counting rule.
  const coverage = useMemo(() => {
    const byPhase = new Map<string, Coverage>();
    const total: Coverage = { crewNamed: 0, crewPlanned: 0, ironNamed: 0, ironPlanned: 0 };
    (phases.data ?? []).forEach((ph) => {
      const cSlots = crewSlotsByPhase.get(ph.id) ?? [];
      const cRows = crewByPhase.get(ph.id) ?? [];
      const eSlots = equipSlotsByPhase.get(ph.id) ?? [];
      const eRows = unitsByPhase.get(ph.id) ?? [];
      const cov: Coverage = {
        crewNamed: cSlots.reduce((sum, slot) =>
          sum + cRows.filter((r) => (r.role_key ?? NO_ROLE) === slot.role_key).length, 0),
        crewPlanned: cSlots.reduce((sum, slot) => sum + Number(slot.planned || 0), 0),
        ironNamed: eSlots.reduce((sum, slot) =>
          sum + eRows.filter((r) => (r.equipment_key ?? NO_SLOT) === slot.equipment_key).length, 0),
        ironPlanned: eSlots.reduce((sum, slot) => sum + Number(slot.planned || 0), 0),
      };
      byPhase.set(ph.id, cov);
      total.crewNamed += cov.crewNamed;
      total.crewPlanned += cov.crewPlanned;
      total.ironNamed += cov.ironNamed;
      total.ironPlanned += cov.ironPlanned;
    });
    return { byPhase, total };
  }, [phases.data, crewByPhase, crewSlotsByPhase, equipSlotsByPhase, unitsByPhase]);

  const assignCrew = useMutation({
    mutationFn: ({ phaseId, employeeId, roleKey }:
      { phaseId: string; employeeId: string; roleKey: string | null }) =>
      PipelineProjectService.assignPhaseCrew(matterid, phaseId, employeeId, roleKey),
    onSuccess: (_data, { phaseId }) => {
      setCrewError(null);
      setCrewPicks((p) => ({ ...p, [phaseId]: '' }));
      void queryClient.invalidateQueries({ queryKey: ['ProjectPhaseCrew', matterid] });
    },
    onError: (err: unknown) =>
      setCrewError((err as Error)?.message || 'Could not assign that person to the phase.'),
  });

  const removeCrew = useMutation({
    mutationFn: ({ phaseId, employeeId, roleKey }:
      { phaseId: string; employeeId: string; roleKey: string | null }) =>
      PipelineProjectService.unassignPhaseCrew(matterid, phaseId, employeeId, roleKey),
    onSuccess: () => {
      setCrewError(null);
      void queryClient.invalidateQueries({ queryKey: ['ProjectPhaseCrew', matterid] });
    },
    onError: (err: unknown) =>
      setCrewError((err as Error)?.message || 'Could not remove that person from the phase.'),
  });

  const assignUnit = useMutation({
    mutationFn: ({ phaseId, inventoryItemId, equipmentKey }:
      { phaseId: string; inventoryItemId: string; equipmentKey: string | null }) =>
      PipelineProjectService.assignPhaseEquipment(matterid, phaseId, inventoryItemId, equipmentKey),
    onSuccess: (_data, { phaseId }) => {
      setEquipError(null);
      setUnitPicks((p) => ({ ...p, [phaseId]: '' }));
      void queryClient.invalidateQueries({ queryKey: ['ProjectPhaseEquipment', matterid] });
    },
    onError: (err: unknown) =>
      setEquipError((err as Error)?.message || 'Could not assign that unit to the phase.'),
  });

  const removeUnit = useMutation({
    mutationFn: ({ phaseId, inventoryItemId, equipmentKey }:
      { phaseId: string; inventoryItemId: string; equipmentKey: string | null }) =>
      PipelineProjectService.unassignPhaseEquipment(
        matterid, phaseId, inventoryItemId, equipmentKey),
    onSuccess: () => {
      setEquipError(null);
      void queryClient.invalidateQueries({ queryKey: ['ProjectPhaseEquipment', matterid] });
    },
    onError: (err: unknown) =>
      setEquipError((err as Error)?.message || 'Could not remove that unit from the phase.'),
  });

  const loading = phases.isLoading || crew.isLoading || equipment.isLoading || fleet.isLoading;
  const failed = phases.isError || crew.isError || equipment.isError || fleet.isError;

  // Build L: the rosters render on the bid-table idiom — ONE table per axis, slot-GROUP
  // rows carrying the labels and count disclosures (an empty slot still discloses), member
  // rows in the identity/slot/evidence/actions column family. Every disclosure STRING is
  // the old panels' verbatim; only its cell moved.
  const crewGroupRow = (key: string, label: ReactNode) => (
    <tr key={`g-${key}`} className="pipeline-roster-group">
      <td colSpan={mayManage ? 4 : 3}>{label}</td>
    </tr>
  );

  const crewMemberRow = (
    phaseId: string, phaseName: string, member: PhaseCrewMember, slotLabel: string,
  ) => (
    <tr key={`${phaseId}-${member.role_key ?? NO_ROLE}-${member.employeeid}`}>
      <td>{member.name || member.employeeid}</td>
      <td className="pipeline-roster-slot-cell">{slotLabel}</td>
      <td>
        {!member.on_team && (
          <span className="pipeline-phase-crew-stranded">no longer on the team</span>
        )}
      </td>
      {mayManage && (
        <td>
          <button
            type="button"
            className="pipeline-secondary-btn"
            onClick={() => removeCrew.mutate({
              phaseId, employeeId: member.employeeid, roleKey: member.role_key })}
            disabled={removeCrew.isPending
              && removeCrew.variables?.phaseId === phaseId
              && removeCrew.variables?.employeeId === member.employeeid
              && (removeCrew.variables?.roleKey ?? null) === (member.role_key ?? null)}
            aria-label={`Remove ${member.name || member.employeeid} from ${phaseName}`}
          >
            Remove
          </button>
        </td>
      )}
    </tr>
  );

  const unitGroupRow = (key: string, label: ReactNode) => (
    <tr key={`g-${key}`} className="pipeline-roster-group">
      <td colSpan={mayManage ? 4 : 3}>{label}</td>
    </tr>
  );

  const unitMemberRow = (
    phaseId: string, phaseName: string, unit: PhaseEquipmentUnit, slotKey: string,
    slotLabel: string,
  ) => (
    <tr key={`${phaseId}-${unit.equipment_key ?? NO_SLOT}-${unit.inventory_item_id}`}>
      <td>{unitLabel(unit.name, unit.asset_tag) || unit.inventory_item_id}</td>
      <td className="pipeline-roster-slot-cell">{slotLabel}</td>
      <td>
        {!unit.unit_active && (
          <span className="pipeline-phase-equipment-note">retired</span>
        )}
        {isCatalogKey(slotKey) && unit.unit_equipment_item_id !== slotKey && (
          <span className="pipeline-phase-equipment-note">different catalog type</span>
        )}
        {unit.sheet_count > 0 && (
          <span className="pipeline-muted pipeline-phase-equipment-sheets">
            seen on {unit.sheet_count} day sheet{unit.sheet_count === 1 ? '' : 's'}
          </span>
        )}
      </td>
      {mayManage && (
        <td>
          <button
            type="button"
            className="pipeline-secondary-btn"
            onClick={() => removeUnit.mutate({
              phaseId,
              inventoryItemId: unit.inventory_item_id,
              equipmentKey: unit.equipment_key })}
            disabled={removeUnit.isPending
              && removeUnit.variables?.phaseId === phaseId
              && removeUnit.variables?.inventoryItemId === unit.inventory_item_id
              && (removeUnit.variables?.equipmentKey ?? null) === (unit.equipment_key ?? null)}
            aria-label={
              `Remove ${unitLabel(unit.name, unit.asset_tag) || unit.inventory_item_id} `
              + `from ${phaseName}`}
          >
            Remove
          </button>
        </td>
      )}
    </tr>
  );

  const crewContent = (phase: { id: string; name: string }) => {
    const assigned = crewByPhase.get(phase.id) ?? [];
    const slots = crewSlotsByPhase.get(phase.id) ?? [];

    const byRole = new Map<string, PhaseCrewMember[]>();
    assigned.forEach((m) => {
      const key = m.role_key ?? NO_ROLE;
      const list = byRole.get(key) ?? [];
      list.push(m);
      byRole.set(key, list);
    });
    const plannedKeys = new Set(slots.map((s) => s.role_key));
    // Assignments whose key the baseline no longer plans (a retired catalog role, an old key)
    // keep rendering under a disclosed group — never a blank, never a dropped row.
    const extraKeys = [...byRole.keys()]
      .filter((k) => k !== NO_ROLE && !plannedKeys.has(k))
      .sort();
    const noRole = byRole.get(NO_ROLE) ?? [];

    const rolePick = rolePicks[phase.id] ?? NO_ROLE;
    const holders = new Set((byRole.get(rolePick) ?? []).map((m) => m.employeeid));
    const available = teamMembers.filter((m) => !holders.has(m.employeeId));
    const pick = crewPicks[phase.id] ?? '';

    const formOpen = crewFormOpen[phase.id] ?? false;
    const cols = mayManage ? 4 : 3;

    return (
      <>
        <div className="bid-items-head">
          <h4 className="bid-items-title">Crew</h4>
          {mayManage && (
            <button
              type="button"
              className="bid-add-row"
              onClick={() =>
                setCrewFormOpen((p) => ({ ...p, [phase.id]: !(p[phase.id] ?? false) }))}
            >
              + Assign person
            </button>
          )}
        </div>
        <div className="bid-table-wrap">
          <table className="bid-table pipeline-roster-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Evidence</th>
                {mayManage && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {assigned.length === 0 && slots.length === 0 && (
                <tr>
                  <td colSpan={cols} className="bid-table-empty">Nobody assigned yet.</td>
                </tr>
              )}
              {slots.map((slot) => {
                const members = byRole.get(slot.role_key) ?? [];
                return [
                  crewGroupRow(`${phase.id}-${slot.role_key}`, (
                    <>
                      <span className="pipeline-roster-slot">{slot.label}</span>
                      <span className="pipeline-muted">
                        {' — '}
                        {countText(members.length, slot.planned)}
                      </span>
                    </>
                  )),
                  ...members.map((m) => crewMemberRow(phase.id, phase.name, m, slot.label)),
                ];
              })}
              {extraKeys.map((key) => {
                const members = byRole.get(key) ?? [];
                const label = key.startsWith('label:')
                  ? (key.slice('label:'.length) || '(unlabelled)')
                  : 'Role no longer planned';
                return [
                  crewGroupRow(`${phase.id}-${key}`, (
                    <>
                      <span className="pipeline-roster-slot">{label}</span>
                      <span className="pipeline-muted">
                        {' — '}
                        {members.length} assigned · not in plan
                      </span>
                    </>
                  )),
                  ...members.map((m) => crewMemberRow(phase.id, phase.name, m, label)),
                ];
              })}
              {/* The group row renders whenever ANY group precedes it (review fold): in the
                  one-table idiom, headerless rows under an orphan group would visually
                  adopt into it — the old any-slots condition predates the shared tbody. */}
              {noRole.length > 0 && [
                ...((slots.length > 0 || extraKeys.length > 0)
                  ? [crewGroupRow(`${phase.id}-no-role`, (
                    <span className="pipeline-roster-slot">No specific role</span>
                  ))]
                  : []),
                ...noRole.map((m) => crewMemberRow(phase.id, phase.name, m, '—')),
              ]}
            </tbody>
          </table>
        </div>

        {mayManage && formOpen && (
          <form
            className="pipeline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (pick) {
                assignCrew.mutate({
                  phaseId: phase.id, employeeId: pick, roleKey: rolePick || null });
              }
            }}
          >
            <div className="pipeline-form-row">
              <label>
                Assign to {phase.name}
                <select
                  value={pick}
                  onChange={(e) => setCrewPicks((p) => ({ ...p, [phase.id]: e.target.value }))}
                  disabled={available.length === 0}
                >
                  <option value="">
                    {available.length === 0
                      ? 'Everyone on the team already fills this slot'
                      : 'Select a team member…'}
                  </option>
                  {available.map((m) => (
                    <option key={m.employeeId} value={m.employeeId}>{m.name}</option>
                  ))}
                </select>
              </label>
              {slots.length > 0 && (
                <label>
                  Role
                  <select
                    value={rolePick}
                    onChange={(e) => {
                      // Changing the slot re-filters the pool, so the person pick resets —
                      // otherwise a now-hidden pick could ride a submit under the placeholder.
                      setRolePicks((p) => ({ ...p, [phase.id]: e.target.value }));
                      setCrewPicks((p) => ({ ...p, [phase.id]: '' }));
                    }}
                  >
                    <option value={NO_ROLE}>No specific role</option>
                    {slots.map((slot) => (
                      <option key={slot.role_key} value={slot.role_key}>{slot.label}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="pipeline-form-actions">
              <button
                type="submit"
                className="pipeline-action-btn"
                disabled={!pick
                  || (assignCrew.isPending && assignCrew.variables?.phaseId === phase.id)}
              >
                {/* "person"/"unit" disambiguate the two axes' submit buttons — one phase
                    container now holds BOTH forms (and it is Build L's affordance naming). */}
                Assign person
              </button>
            </div>
          </form>
        )}
      </>
    );
  };

  const equipContent = (phase: { id: string; name: string }) => {
    const assigned = unitsByPhase.get(phase.id) ?? [];
    const slots = equipSlotsByPhase.get(phase.id) ?? [];
    const typeSlots = typeSlotsByPhase.get(phase.id) ?? [];

    const bySlot = new Map<string, PhaseEquipmentUnit[]>();
    assigned.forEach((u) => {
      const key = u.equipment_key ?? NO_SLOT;
      const list = bySlot.get(key) ?? [];
      list.push(u);
      bySlot.set(key, list);
    });
    // Type-slot keys count as planned (Build T) — without them every type-keyed
    // assignment would ALSO render under "Slot no longer planned", doubled.
    const plannedKeys = new Set([
      ...slots.map((s) => s.equipment_key),
      ...typeSlots.map((s) => s.equipment_key),
    ]);
    // Assignments whose key the baseline no longer plans (a retired catalog type, an old key)
    // keep rendering under a disclosed group — never a blank, never a dropped row.
    const extraKeys = [...bySlot.keys()]
      .filter((k) => k !== NO_SLOT && !plannedKeys.has(k))
      .sort();
    const noSlot = bySlot.get(NO_SLOT) ?? [];

    const slotPick = slotPicks[phase.id] ?? NO_SLOT;
    const holders = new Set((bySlot.get(slotPick) ?? []).map((u) => u.inventory_item_id));
    // Type-matched units first when a catalog slot is picked; the rest stay offered with the
    // substitution disclosure — sorted after, never hidden.
    const available = units
      .filter((u) => !holders.has(u.id))
      .sort((a, b) => {
        if (isCatalogKey(slotPick)) {
          const aMatch = a.equipment_item_id === slotPick ? 0 : 1;
          const bMatch = b.equipment_item_id === slotPick ? 0 : 1;
          if (aMatch !== bMatch) return aMatch - bMatch;
        }
        return unitLabel(a.name, a.asset_tag).localeCompare(unitLabel(b.name, b.asset_tag));
      });
    const pick = unitPicks[phase.id] ?? '';

    const formOpen = equipFormOpen[phase.id] ?? false;
    const cols = mayManage ? 4 : 3;

    return (
      <>
        <div className="bid-items-head">
          <h4 className="bid-items-title">Equipment</h4>
          {mayManage && (
            <button
              type="button"
              className="bid-add-row"
              onClick={() =>
                setEquipFormOpen((p) => ({ ...p, [phase.id]: !(p[phase.id] ?? false) }))}
            >
              + Assign unit
            </button>
          )}
        </div>
        <div className="bid-table-wrap">
          <table className="bid-table pipeline-roster-table">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Slot</th>
                <th>Evidence</th>
                {mayManage && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {assigned.length === 0 && slots.length === 0 && typeSlots.length === 0 && (
                <tr>
                  <td colSpan={cols} className="bid-table-empty">No equipment assigned yet.</td>
                </tr>
              )}
              {slots.map((slot) => {
                const members = bySlot.get(slot.equipment_key) ?? [];
                return [
                  unitGroupRow(`${phase.id}-${slot.equipment_key}`, (
                    <>
                      <span className="pipeline-roster-slot">{slot.label}</span>
                      <span className="pipeline-muted">
                        {' — '}
                        {countText(members.length, slot.planned)}
                      </span>
                    </>
                  )),
                  ...members.map((u) => unitMemberRow(
                    phase.id, phase.name, u, slot.equipment_key, slot.label)),
                ];
              })}
              {/* TARGET-LESS type slots (Build T, plan mob-loads-categories-and-type-slots):
                  the frozen load matrix's TYPE axis — which iron rides the mob/demob loads.
                  No "of m" and no badge: the matrix counts LOADS, not units, so a quota
                  would lie; the loads render as muted disclosure and zero renders nothing. */}
              {typeSlots.map((slot) => {
                const members = bySlot.get(slot.equipment_key) ?? [];
                const loads = loadsText(slot.loads);
                return [
                  unitGroupRow(`${phase.id}-${slot.equipment_key}`, (
                    <>
                      <span className="pipeline-roster-slot">{slot.label}</span>
                      <span className="pipeline-muted">
                        {' — '}
                        {loads ? `${loads} · ` : ''}
                        {members.length} named
                      </span>
                    </>
                  )),
                  ...members.map((u) => unitMemberRow(
                    phase.id, phase.name, u, slot.equipment_key, slot.label)),
                ];
              })}
              {extraKeys.map((key) => {
                const members = bySlot.get(key) ?? [];
                // label: and type: orphans both carry their name IN the key — recover it,
                // like the planned render would; only a catalog-uuid orphan has nothing to
                // show.
                const label = key.startsWith('label:')
                  ? (key.slice('label:'.length) || '(unlabelled)')
                  : key.startsWith('type:')
                    ? (key.slice('type:'.length) || '(unlabelled)')
                    : 'Slot no longer planned';
                return [
                  unitGroupRow(`${phase.id}-${key}`, (
                    <>
                      <span className="pipeline-roster-slot">{label}</span>
                      <span className="pipeline-muted">
                        {' — '}
                        {members.length} assigned · not in plan
                      </span>
                    </>
                  )),
                  ...members.map((u) => unitMemberRow(phase.id, phase.name, u, key, label)),
                ];
              })}
              {/* Same review fold as the crew side: any preceding group forces the header. */}
              {noSlot.length > 0 && [
                ...((slots.length > 0 || typeSlots.length > 0 || extraKeys.length > 0)
                  ? [unitGroupRow(`${phase.id}-no-slot`, (
                    <span className="pipeline-roster-slot">No specific slot</span>
                  ))]
                  : []),
                ...noSlot.map((u) => unitMemberRow(phase.id, phase.name, u, NO_SLOT, '—')),
              ]}
            </tbody>
          </table>
        </div>

        {mayManage && formOpen && (
          <form
            className="pipeline-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (pick) {
                assignUnit.mutate({
                  phaseId: phase.id, inventoryItemId: pick, equipmentKey: slotPick || null });
              }
            }}
          >
            <div className="pipeline-form-row">
              <label>
                Assign a unit to {phase.name}
                <select
                  value={pick}
                  onChange={(e) => setUnitPicks((p) => ({ ...p, [phase.id]: e.target.value }))}
                  disabled={available.length === 0}
                >
                  <option value="">
                    {available.length === 0
                      // Honest about WHICH emptiness (plan C3 rider): a tenant with no
                      // tagged units at all was told everything is assigned.
                      ? (units.length === 0
                        ? 'No tagged fleet units — add asset-tagged units in the bid admin’s Inventory catalog'
                        : 'Every fleet unit already fills this slot')
                      : 'Select a unit…'}
                  </option>
                  {available.map((u) => (
                    <option key={u.id} value={u.id}>
                      {unitLabel(u.name, u.asset_tag)}
                      {isCatalogKey(slotPick) && u.equipment_item_id !== slotPick
                        ? ' · different catalog type' : ''}
                    </option>
                  ))}
                </select>
              </label>
              {(slots.length > 0 || typeSlots.length > 0) && (
                <label>
                  Slot
                  <select
                    value={slotPick}
                    onChange={(e) => {
                      // Changing the slot re-filters and re-orders the pool, so the unit pick
                      // resets — a now-hidden pick must never ride a submit.
                      setSlotPicks((p) => ({ ...p, [phase.id]: e.target.value }));
                      setUnitPicks((p) => ({ ...p, [phase.id]: '' }));
                    }}
                  >
                    <option value={NO_SLOT}>No specific slot</option>
                    {slots.map((slot) => (
                      <option key={slot.equipment_key} value={slot.equipment_key}>
                        {slot.label}
                      </option>
                    ))}
                    {typeSlots.map((slot) => (
                      <option key={slot.equipment_key} value={slot.equipment_key}>
                        {slot.label} (type)
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="pipeline-form-actions">
              <button
                type="submit"
                className="pipeline-action-btn"
                disabled={!pick
                  || (assignUnit.isPending && assignUnit.variables?.phaseId === phase.id)}
              >
                Assign unit
              </button>
            </div>
          </form>
        )}
      </>
    );
  };

  const phaseList = phases.data ?? [];
  // A selection whose phase left the list (a refetch, a deletion) falls back to summary —
  // never a blank detail pane.
  const activeView = view !== SUMMARY && phaseList.some((p) => p.id === view) ? view : SUMMARY;
  const visiblePhases = activeView === SUMMARY
    ? phaseList
    : phaseList.filter((p) => p.id === activeView);

  return (
    <section className="pipeline-panel pipeline-phase-planning">
      <div className="pipeline-panel-header">
        <div>
          <h2 className="pipeline-panel-title">Phase planning</h2>
          <p className="pipeline-panel-subtitle">
            Optional. Name who fills each role and which fleet units fill the iron each phase
            was planned for — a record of intent only. It changes no planned figure and is
            never required.
          </p>
        </div>
        <button
          type="button"
          className="pipeline-secondary-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Hide phase planning' : 'Show phase planning'}
        </button>
      </div>

      {expanded && loading && <p className="pipeline-muted">Loading phase planning…</p>}
      {expanded && failed && (
        <p className="pipeline-muted">Phase planning could not be loaded.</p>
      )}

      {expanded && !loading && !failed && phaseList.length === 0 && (
        <p className="pipeline-muted">This project has no phases yet.</p>
      )}

      {expanded && crewError && <p className="pipeline-form-error" role="alert">{crewError}</p>}
      {expanded && equipError && <p className="pipeline-form-error" role="alert">{equipError}</p>}

      {expanded && !loading && !failed && phaseList.length > 0 && (
        <div className="pipeline-phase-planning-body">
          <PhaseRail
            summaryLabel="All phases"
            summaryFigure={coverageText(coverage.total)}
            dividerLabel="Phases"
            activeKey={activeView}
            onSelect={setView}
            items={phaseList.map((p) => ({
              key: p.id,
              name: p.name,
              figure: coverageText(
                coverage.byPhase.get(p.id)
                ?? { crewNamed: 0, crewPlanned: 0, ironNamed: 0, ironPlanned: 0 },
              ),
            }))}
            emptyText="No phases yet."
          />
          <div className="pipeline-phase-planning-detail">
            {visiblePhases.map((phase) => (
              <div key={phase.id} className="pipeline-phase-planning-phase">
                <h3 className="pipeline-panel-subheading">{phase.name}</h3>
                {/* Build L: each axis carries its own bid-items head (title + "+ Assign"),
                    so no separate axis heading. */}
                <div className="pipeline-phase-planning-axis">{crewContent(phase)}</div>
                <div className="pipeline-phase-planning-axis">{equipContent(phase)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default PhasePlanningSection;
